import { Core, Decorative } from "@faire/design-tokens";
import {
  DEPRECATED_backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { LockSquare } from "@faire/web/slate/icons/foundation/Lock";
import { Flex, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Tooltip } from "@faire/web/slate/Tooltip";
import {
  Span as TypographySpan,
  Typography,
} from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { useIsLoggedOutProductTileRedesign } from "@faire/retailer/features/MarketplaceItems/useIsLoggedOutProductTileRedesign";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

import { RedesignedOutOfStockText } from "./__internal__/Header/RedesignedOutOfStockText";

export enum PRICE_TEXT_SURFACE {
  PRODUCT_TILE = "product_tile",
  PDP = "pdp",
  QUICK_ADD_MODAL = "quick_add_modal",
}

interface IPriceTextProps {
  originalPrice: string;
  wholesalePrice: string;
  retailPrice: string;
  small?: boolean;
  isOutOfStock?: boolean;
  showOutOfStockUnderProductName?: boolean;
  isTilePricing?: boolean;
  priceTextSurface?: PRICE_TEXT_SURFACE;
}

const RecommendedRetailPricePromoText: React.FC<IPriceTextProps> = ({
  originalPrice,
  wholesalePrice,
  retailPrice,
  small,
}) => {
  const renderPromoFormatting = (chunk: JSX.Element) => (
    <TypographySpan
      color={Decorative.shade.orange}
      variant={small ? "paragraphSansMedium" : "subheadingSansMedium"}
    >
      {chunk}
    </TypographySpan>
  );

  return (
    <StrictLocalMsg
      defaultMessage="<PromoFormatting>{wholesaleAmountMoney}</PromoFormatting> <StrikeThroughFormatting>{originalAmountMoney}</StrikeThroughFormatting> <TextFormatting>RRP {retailAmountMoney}</TextFormatting>"
      values={{
        PromoFormatting: renderPromoFormatting,
        StrikeThroughFormatting: renderStrikeThroughFormatting,
        TextFormatting: renderRegularChunkGrey,
        originalAmountMoney: originalPrice,
        wholesaleAmountMoney: wholesalePrice,
        retailAmountMoney: retailPrice,
      }}
      description={{
        text: "Original wholesale price, discounted wholesale price through product-level promotion,and manufacturer's suggested retail price of the product",
      }}
    />
  );
};

const PromoText: React.FC<IPriceTextProps> = ({
  originalPrice,
  wholesalePrice,
  retailPrice,
  isTilePricing,
  small,
}) => {
  const renderPromoFormatting = (chunk: JSX.Element) => (
    <TypographySpan
      color={Decorative.shade.orange}
      variant={small ? "paragraphSansMedium" : "subheadingSansMedium"}
    >
      {chunk}
    </TypographySpan>
  );

  return (
    <StrictLocalMsg
      defaultMessage="<PromoFormatting>{wholesaleAmountMoney}</PromoFormatting> <StrikeThroughFormatting>{originalAmountMoney}</StrikeThroughFormatting>
<TextFormatting>MSRP {retailAmountMoney}</TextFormatting>"
      values={{
        PromoFormatting: renderPromoFormatting,
        TextFormatting: isTilePricing
          ? renderRegularChunkGrey
          : renderRegularChunk,
        StrikeThroughFormatting: renderStrikeThroughFormatting,
        originalAmountMoney: originalPrice,
        wholesaleAmountMoney: wholesalePrice ?? "",
        retailAmountMoney: retailPrice,
      }}
      description={{
        text: "Original wholesale price, discounted wholesale price through product-level promotion,and manufacturer's suggested retail price of the product",
      }}
    />
  );
};

const RecommendedRetailPriceText: React.FC<
  Pick<IPriceTextProps, "wholesalePrice" | "retailPrice" | "small">
> = ({ wholesalePrice, retailPrice, small }) => {
  return (
    <StrictLocalMsg
      defaultMessage="<BoldFormatting>{wholesaleAmountMoney}</BoldFormatting> <TextFormatting>RRP {retailAmountMoney}</TextFormatting>"
      values={{
        TextFormatting: renderRegularChunkGrey,
        BoldFormatting: small
          ? renderParagraphBoldFormatting
          : renderSubHeadingBoldFormatting,
        wholesaleAmountMoney: wholesalePrice ?? "",
        retailAmountMoney: retailPrice,
      }}
      description={{
        text: "Wholesale price and manufacturer's suggested retail price of the product",
      }}
    />
  );
};

const PriceText: React.FC<
  Pick<
    IPriceTextProps,
    "wholesalePrice" | "retailPrice" | "isTilePricing" | "priceTextSurface"
  >
> = ({ wholesalePrice, retailPrice, isTilePricing, priceTextSurface }) => {
  const getWholesaleTextFormatting = () => {
    if (priceTextSurface === PRICE_TEXT_SURFACE.PDP) {
      return (chunk: JSX.Element) =>
        renderSubHeadingBoldFormatting(chunk, "4px");
    }
    return renderRegularChunk;
  };

  return (
    <StrictLocalMsg
      defaultMessage="<WholesaleTextFormatting>{wholesaleAmountMoney}</WholesaleTextFormatting> <TextFormatting>MSRP {retailAmountMoney}</TextFormatting>"
      values={{
        TextFormatting: isTilePricing
          ? renderRegularChunkGrey
          : renderRegularChunk,
        WholesaleTextFormatting: getWholesaleTextFormatting(),
        wholesaleAmountMoney: wholesalePrice ?? "",
        retailAmountMoney: retailPrice,
      }}
      description={{
        text: "Wholesale price and manufacturer's suggested retail price of the product",
      }}
    />
  );
};

const PriceBase: React.FC<
  IPriceTextProps & { showProductPromotions: boolean }
> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const { isUK: isUKRetailer } = useRetailerStore(["isUK"]);
  const isLoggedOutProductTileDesign = useIsLoggedOutProductTileRedesign();

  if (props.showProductPromotions) {
    return (
      <Row
        gap="1x"
        align="center"
        data-test-id="promotion-text"
        aria-label={strictLocalize(
          {
            defaultMessage:
              "original price is {originalAmountMoney}, discounted price is {wholesaleAmountMoney}",
            description: {
              text: "aria-label for stating price",
              img: "https://cdn.faire.com/fastly/357091ed2d43c92e5e558a48f9462f4de5ac89689c208bbc460682d9a2552163.png",
            },
          },
          DEPRECATED_backwardsCompatibleValues({
            originalAmountMoney: props.originalPrice,
            wholesaleAmountMoney: props.wholesalePrice,
          })
        )}
      >
        <Typography>
          {isUKRetailer ? (
            <RecommendedRetailPricePromoText {...props} />
          ) : (
            <PromoText {...props} />
          )}
        </Typography>
      </Row>
    );
  } else {
    return (
      <Row gap="1x">
        {isLoggedOutProductTileDesign ? (
          <Row align="center" gap={4}>
            <Typography>
              <StrictLocalMsg
                defaultMessage="WSP"
                description={{ text: "WSP stands for wholesale price" }}
              />
            </Typography>
            <LockIconMaybeTooltip />
          </Row>
        ) : null}
        <Typography>
          {isUKRetailer ? (
            <RecommendedRetailPriceText {...props} />
          ) : (
            <PriceText {...props} />
          )}
        </Typography>
      </Row>
    );
  }
};

export const Price: React.FC<
  IPriceTextProps & { showProductPromotions: boolean }
> = (props) => {
  return (
    <Row align="center" gap="1x" data-test-id="product-price">
      <PriceBase {...props} />
      <RedesignedOutOfStockText
        isOutOfStock={props.isOutOfStock}
        showOutOfStock={!props.showOutOfStockUnderProductName}
        dataTestId="out-of-stock-text-in-Price"
      />
    </Row>
  );
};

const LockIconMaybeTooltip: React.FC = () => {
  const { isTabletAndBelow } = useViewport();
  if (isTabletAndBelow) {
    return <LockSquare width="12px" height="12px" />;
  }
  return (
    <Flex style={{ position: "relative" }}>
      <LockSquare style={{ position: "absolute" }} width="12px" height="12px" />
      <StyledTooltip
        position="top"
        pointerOffset={-60}
        targetNode={
          <div
            style={{ height: "12px", width: "12px", color: "transparent" }}
          />
        }
        width={320}
      >
        <Typography color={Core.text.inverse}>
          <StrictLocalMsg
            defaultMessage="Join Faire to unlock wholesale pricing."
            description={{
              text: "Text to encourage sign-up",
              img: "https://cdn.faire.com/fastly/4f2ca7439ee2d0ac17a9625f104d1f40b3c6dd6be83dd9bc6d3d4b5cdc86a666.png",
            }}
          />
        </Typography>
        <Spacer size="2x" />
        <Typography color={Core.text.inverse}>
          <u>
            <StrictLocalMsg
              defaultMessage="Sign up for free"
              description={{
                text: "Text to encourage sign-up",
                img: "https://cdn.faire.com/fastly/4f2ca7439ee2d0ac17a9625f104d1f40b3c6dd6be83dd9bc6d3d4b5cdc86a666.png",
              }}
            />
          </u>
        </Typography>
      </StyledTooltip>
    </Flex>
  );
};

const renderRegularChunkGrey = (chunk: JSX.Element) => (
  <TypographySpan color={Core.text.subdued} variant="paragraphSansRegular">
    {chunk}
  </TypographySpan>
);

const renderRegularChunk = (chunk: JSX.Element) => (
  <TypographySpan variant="paragraphSansRegular">{chunk}</TypographySpan>
);

const renderStrikeThroughFormatting = (chunk: JSX.Element) => (
  <StyledStrikethrough>{chunk}</StyledStrikethrough>
);

const renderParagraphBoldFormatting = (chunk: JSX.Element) => (
  <TypographySpan variant="paragraphSansMedium">{chunk}</TypographySpan>
);

const renderSubHeadingBoldFormatting = (
  chunk: JSX.Element,
  marginRight?: string
) => (
  <TypographySpan
    variant="subheadingSansMedium"
    style={{ marginRight: marginRight }}
  >
    {chunk}
  </TypographySpan>
);

const StyledStrikethrough = styled(TypographySpan).attrs({
  variant: "paragraphSansRegular",
})`
  text-decoration: line-through;
`;

const StyledTooltip = styled(Tooltip)`
  z-index: 10;
  position: "absolute";
`;
